import { PLATFORM_LANGUAGE_OPTIONS, languageLabel } from './platformLanguages';
import { validatePersonName, validateLocalPhoneForCountry, validateEmail } from '../utils/personFieldValidation';
import { composeE164, localPhoneDigits, splitPhoneByDialCodes } from '../utils/phoneDialCode';
import { phoneLengthForDialCode, getCachedCountryDialCodes } from '../services/countryDialCodeService';

export const USER_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'customer', label: 'Customer' },
  { value: 'driver', label: 'Driver' },
  { value: 'delivery_partner', label: 'Delivery Partner' },
  { value: 'merchant', label: 'Merchant' },
  { value: 'service_provider', label: 'Service Provider' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'employee', label: 'Employee' },
  { value: 'client_admin', label: 'Admin' },
] as const;

export const USER_TYPE_FORM_OPTIONS = [
  { value: 'customer', label: 'Customer', group: 'End users' },
  { value: 'driver', label: 'Driver / Taxi Partner', group: 'Partners' },
  { value: 'delivery_partner', label: 'Delivery Partner', group: 'Partners' },
  { value: 'service_provider', label: 'On-Demand Service Provider', group: 'Partners' },
  { value: 'employee', label: 'Employee / Staff', group: 'Internal' },
  { value: 'client_admin', label: 'Client Admin', group: 'Internal' },
] as const;

export const USER_STATUS_OPTIONS = [
  { value: '', label: 'All', color: 'default' as const },
  { value: 'active', label: 'Active', color: 'success' as const },
  { value: 'inactive', label: 'Inactive', color: 'default' as const },
  { value: 'blocked', label: 'Blocked', color: 'error' as const },
  { value: 'unverified', label: 'Unverified', color: 'warning' as const },
];

export const USER_TYPE_LABELS: Record<string, string> = {
  customer: 'Customer',
  driver: 'Driver',
  delivery_partner: 'Delivery Partner',
  merchant: 'Merchant',
  service_provider: 'Service Provider',
  vendor: 'Vendor',
  client_admin: 'Admin',
  employee: 'Employee',
};

export const GENDER_OPTIONS = [
  { value: '', label: 'Prefer not to say' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

export { PLATFORM_LANGUAGE_OPTIONS as PREFERRED_LANGUAGE_OPTIONS, languageLabel };

/** Map stored language (code or legacy name) to platform language code. */
export function normalizeUserLanguage(raw: string): string {
  if (!raw?.trim()) return '';
  const lower = raw.trim().toLowerCase();
  const byCode = PLATFORM_LANGUAGE_OPTIONS.find((l) => l.code === lower);
  if (byCode) return byCode.code;
  const byName = PLATFORM_LANGUAGE_OPTIONS.find(
    (l) => l.name.toLowerCase() === lower || l.nativeName.toLowerCase() === lower,
  );
  return byName?.code || raw.trim();
}

export type UserFormTabId = 'profile' | 'assignment' | 'partner' | 'documents' | 'access' | 'security' | 'notes';

export const USER_FORM_TAB_META: Record<UserFormTabId, { title: string; subtitle: string }> = {
  profile: { title: 'Profile', subtitle: 'Name, contact, avatar, and identity details' },
  assignment: { title: 'Zone & Hub', subtitle: 'Service hub and operating zone for this user' },
  partner: { title: 'Partner Details', subtitle: 'Business, vehicle, referral, and commission settings' },
  documents: { title: 'Documents', subtitle: 'Verify uploaded documents and approve the driver for the app' },
  access: { title: 'Access & Roles', subtitle: 'Staff roles, department, and permissions' },
  security: { title: 'Login & security', subtitle: 'Web admin password — employee and admin only' },
  notes: { title: 'Address & Notes', subtitle: 'Location, preferences, and internal admin notes' },
};

export const USER_TYPE_ROLE_HINTS: Record<string, { summary: string; steps: string }> = {
  customer: { summary: 'Mobile app user — signs in with phone OTP (no web password).', steps: 'Name, phone, optional email, address' },
  driver: { summary: 'Partner app driver — signs in with phone OTP (no web password).', steps: 'Contact, zone, vehicle, documents' },
  delivery_partner: { summary: 'Partner app delivery — signs in with phone OTP (no web password).', steps: 'Contact, zone, vehicle, documents' },
  merchant: { summary: 'Store or restaurant owner on the delivery hub.', steps: 'Contact, zone, business, documents' },
  service_provider: { summary: 'Provider app — signs in with phone OTP (no web password).', steps: 'Contact, zone, skills/business, documents' },
  vendor: { summary: 'Event organizer or ticket vendor.', steps: 'Contact, zone, business, documents' },
  employee: { summary: 'Staff who log into the admin web panel.', steps: 'Contact, zones, roles, and web password' },
  client_admin: { summary: 'Tenant admin who logs into the admin web panel.', steps: 'Contact, roles, and web password' },
};

export interface UserTypeFormConfig {
  tabs: UserFormTabId[];
  requiresZone: boolean;
  requiresServiceHub: boolean;
  suggestRoles: boolean;
  /** Phone OTP / mobile app login — no web password section */
  mobileLoginOnly: boolean;
  defaultServiceHub?: string;
}

export const USER_TYPE_FORM_CONFIG: Record<string, UserTypeFormConfig> = {
  customer: {
    tabs: ['profile', 'notes'],
    requiresZone: false,
    requiresServiceHub: false,
    suggestRoles: false,
    mobileLoginOnly: true,
  },
  driver: {
    tabs: ['profile', 'assignment', 'partner', 'documents', 'notes'],
    requiresZone: true,
    requiresServiceHub: true,
    suggestRoles: false,
    mobileLoginOnly: true,
    defaultServiceHub: 'transport',
  },
  delivery_partner: {
    tabs: ['profile', 'assignment', 'partner', 'documents', 'notes'],
    requiresZone: true,
    requiresServiceHub: true,
    suggestRoles: false,
    mobileLoginOnly: true,
    defaultServiceHub: 'delivery',
  },
  merchant: {
    tabs: ['profile', 'assignment', 'partner', 'documents', 'notes'],
    requiresZone: true,
    requiresServiceHub: true,
    suggestRoles: false,
    mobileLoginOnly: true,
    defaultServiceHub: 'delivery',
  },
  service_provider: {
    tabs: ['profile', 'assignment', 'partner', 'documents', 'notes'],
    requiresZone: true,
    requiresServiceHub: true,
    suggestRoles: false,
    mobileLoginOnly: true,
    defaultServiceHub: 'on_demand',
  },
  vendor: {
    tabs: ['profile', 'assignment', 'partner', 'documents', 'notes'],
    requiresZone: true,
    requiresServiceHub: true,
    suggestRoles: false,
    mobileLoginOnly: true,
    defaultServiceHub: 'booking',
  },
  employee: {
    tabs: ['profile', 'assignment', 'access', 'security', 'notes'],
    requiresZone: false,
    requiresServiceHub: false,
    suggestRoles: true,
    mobileLoginOnly: false,
  },
  client_admin: {
    tabs: ['profile', 'access', 'security', 'notes'],
    requiresZone: false,
    requiresServiceHub: false,
    suggestRoles: true,
    mobileLoginOnly: false,
  },
};

export function getFormTabsForUserType(userType: string): UserFormTabId[] {
  return USER_TYPE_FORM_CONFIG[userType]?.tabs || USER_TYPE_FORM_CONFIG.customer.tabs;
}

export function getUserTypeFormConfig(userType: string): UserTypeFormConfig {
  return USER_TYPE_FORM_CONFIG[userType] || USER_TYPE_FORM_CONFIG.customer;
}

export function getFormTabLabels(userType: string): string[] {
  return getFormTabsForUserType(userType).map((id) => USER_FORM_TAB_META[id].title);
}

export const PARTNER_USER_TYPES = new Set([
  'driver',
  'delivery_partner',
  'merchant',
  'service_provider',
  'vendor',
]);

export const STAFF_USER_TYPES = new Set(['employee', 'client_admin']);

/** Customer + partners — mobile OTP login; no web password. */
export function isMobileAppUserType(userType: string): boolean {
  return getUserTypeFormConfig(userType).mobileLoginOnly === true;
}

/** Employee / client_admin — admin panel web login with password. */
export function isWebStaffUserType(userType: string): boolean {
  return STAFF_USER_TYPES.has(userType);
}

export const PARTNER_ENTITY_BY_USER_TYPE: Record<string, string> = {
  driver: 'driver',
  delivery_partner: 'delivery_partner',
  merchant: 'merchant',
  service_provider: 'service_provider',
  vendor: 'vendor',
};

/** @deprecated use USER_FORM_TAB_META + getFormTabsForUserType */
export const USER_FORM_TABS = Object.values(USER_FORM_TAB_META).map((t) => ({
  title: t.title,
  subtitle: t.subtitle,
}));

export interface UserRow {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  phoneCountryCode?: string;
  userType?: string;
  avatar?: string;
  isActive?: boolean;
  isBlocked?: boolean;
  blockedReason?: string;
  isVerified?: boolean;
  lastLogin?: string;
  lastLoginIp?: string;
  createdAt?: string;
  zoneId?: string | { _id: string; name?: string; code?: string };
  zoneIds?: Array<string | { _id: string; name?: string; code?: string }>;
  serviceHub?: string;
  partnerEntityType?: string;
  partnerStatus?: string;
  roles?: Array<{ _id: string; name?: string; code?: string }>;
  metadata?: Record<string, unknown>;
}

/** Reason stored when an admin blocked this account (list/detail/export). */
export function resolveBlockedReason(row: Pick<UserRow, 'isBlocked' | 'blockedReason' | 'metadata'>): string {
  if (!row.isBlocked) return '';
  const meta = row.metadata || {};
  const candidates = [row.blockedReason, meta.blockedReason, meta.blockReason, meta.blocked_reason];
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return 'No reason recorded';
}

export interface UserFormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  phoneCountryCode: string;
  emergencyPhone: string;
  emergencyPhoneCountryCode: string;
  avatar: string;
  gender: string;
  dateOfBirth: string;
  country: string;
  language: string;
  userType: string;
  roleIds: string[];
  serviceHub: string;
  zoneId: string;
  zoneIds: string[];
  partnerEntityType: string;
  businessName: string;
  vehicleNumber: string;
  referralCode: string;
  commissionPercent: string;
  employeeId: string;
  department: string;
  password: string;
  confirmPassword: string;
  autoVerify: boolean;
  isVerified: boolean;
  isActive: boolean;
  sendWelcomeEmail: boolean;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  notes: string;
}

export function emptyUserForm(overrides?: Partial<UserFormState>): UserFormState {
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    phoneCountryCode: '',
    emergencyPhone: '',
    emergencyPhoneCountryCode: '',
    avatar: '',
    gender: '',
    dateOfBirth: '',
    country: '',
    language: '',
    userType: 'customer',
    roleIds: [],
    serviceHub: '',
    zoneId: '',
    zoneIds: [],
    partnerEntityType: '',
    businessName: '',
    vehicleNumber: '',
    referralCode: '',
    commissionPercent: '',
    employeeId: '',
    department: '',
    password: '',
    confirmPassword: '',
    autoVerify: false,
    isVerified: true,
    isActive: true,
    sendWelcomeEmail: true,
    address: '',
    city: '',
    state: '',
    postalCode: '',
    notes: '',
    ...overrides,
  };
}

export function userRowToForm(row: UserRow): UserFormState {
  const meta = row.metadata || {};
  const zoneId = typeof row.zoneId === 'object' ? row.zoneId?._id : row.zoneId;
  const zoneIds = row.zoneIds?.length
    ? row.zoneIds.map((z) => (typeof z === 'object' ? z._id : z))
    : zoneId ? [zoneId] : [];
  const userType = String(row.userType || 'customer');
  let phone = String(row.phone || '');
  let phoneCountryCode = String(
    row.phoneCountryCode
    || meta.phoneCountryCode
    || '',
  );
  if (phone.startsWith('+')) {
    const split = splitPhoneByDialCodes(phone, getCachedCountryDialCodes().map((o) => o.dialCode));
    if (split.dialCode) {
      phoneCountryCode = phoneCountryCode || split.dialCode;
      phone = split.localNumber;
    }
  } else {
    phone = localPhoneDigits(phone);
  }
  let emergencyPhone = String(meta.emergencyPhone || meta.emergencyNumber || '');
  let emergencyPhoneCountryCode = String(meta.emergencyPhoneCountryCode || '');
  if (emergencyPhone.startsWith('+')) {
    const split = splitPhoneByDialCodes(emergencyPhone, getCachedCountryDialCodes().map((o) => o.dialCode));
    if (split.dialCode) {
      emergencyPhoneCountryCode = emergencyPhoneCountryCode || split.dialCode;
      emergencyPhone = split.localNumber;
    }
  } else {
    emergencyPhone = localPhoneDigits(emergencyPhone);
  }
  return emptyUserForm({
    firstName: String(row.firstName || ''),
    lastName: String(row.lastName || ''),
    email: String(row.email || ''),
    phone,
    phoneCountryCode,
    emergencyPhone,
    emergencyPhoneCountryCode,
    avatar: String(row.avatar || ''),
    gender: String(meta.gender || ''),
    dateOfBirth: String(meta.dateOfBirth || meta.dob || ''),
    country: String(meta.country || ''),
    language: normalizeUserLanguage(String(meta.language || '')),
    userType,
    roleIds: (row.roles || []).map((r) => r._id),
    serviceHub: String(row.serviceHub || ''),
    zoneId: String(zoneId || ''),
    zoneIds,
    partnerEntityType: String(row.partnerEntityType || PARTNER_ENTITY_BY_USER_TYPE[userType] || ''),
    businessName: String(meta.businessName || meta.storeName || ''),
    vehicleNumber: String(meta.vehicleNumber || meta.vehicleNo || ''),
    referralCode: String(meta.referralCode || ''),
    commissionPercent: meta.commissionPercent != null ? String(meta.commissionPercent) : '',
    employeeId: String(meta.employeeId || ''),
    department: String(meta.department || ''),
    password: '',
    confirmPassword: '',
    autoVerify: false,
    isVerified: !!row.isVerified,
    isActive: row.isActive !== false,
    sendWelcomeEmail: false,
    address: String(meta.address || ''),
    city: String(meta.city || ''),
    state: String(meta.state || ''),
    postalCode: String(meta.postalCode || meta.pincode || ''),
    notes: String(meta.adminNotes || meta.notes || ''),
  });
}

export function buildUserPayload(form: UserFormState, isEdit = false): Record<string, unknown> {
  const metadata: Record<string, unknown> = {
    country: form.country || undefined,
    language: form.language || undefined,
    gender: form.gender || undefined,
    dateOfBirth: form.dateOfBirth || undefined,
    emergencyPhone: form.emergencyPhone
      ? composeE164(form.emergencyPhoneCountryCode, form.emergencyPhone)
      : undefined,
    phoneCountryCode: form.phoneCountryCode || undefined,
    emergencyPhoneCountryCode: form.emergencyPhoneCountryCode || undefined,
    address: form.address || undefined,
    city: form.city || undefined,
    state: form.state || undefined,
    postalCode: form.postalCode || undefined,
    adminNotes: form.notes || undefined,
    businessName: form.businessName || undefined,
    vehicleNumber: form.vehicleNumber || undefined,
    referralCode: form.referralCode || undefined,
    commissionPercent: form.commissionPercent ? Number(form.commissionPercent) : undefined,
    employeeId: form.employeeId || undefined,
    department: form.department || undefined,
    sendWelcomeEmail: form.sendWelcomeEmail,
  };

  const payload: Record<string, unknown> = {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim() || undefined,
    email: form.email.trim() || undefined,
    phone: form.phone.trim()
      ? composeE164(form.phoneCountryCode, form.phone)
      : undefined,
    userType: form.userType,
    avatar: form.avatar.trim() || undefined,
    zoneId: form.zoneIds[0] || form.zoneId || undefined,
    zoneIds: form.zoneIds.length ? form.zoneIds : (form.zoneId ? [form.zoneId] : []),
    serviceHub: form.serviceHub || undefined,
    partnerEntityType: form.partnerEntityType || PARTNER_ENTITY_BY_USER_TYPE[form.userType] || undefined,
    isVerified: form.isVerified,
    isActive: form.isActive,
    roleIds: form.roleIds.length ? form.roleIds : [],
    metadata,
  };

  if (form.password.trim()) payload.password = form.password.trim();
  if (!isEdit) {
    payload.autoVerify = form.autoVerify;
  }
  return payload;
}

function isoDateOnly(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Minimum age for this account type (drivers/partners must be 18). */
export function minAgeForUserType(userType: string): number {
  if (PARTNER_USER_TYPES.has(userType) || STAFF_USER_TYPES.has(userType)) return 18;
  return 5;
}

/** Latest allowed date of birth for the given account type. */
export function maxDateOfBirth(userType?: string): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setFullYear(d.getFullYear() - minAgeForUserType(userType || ''));
  return isoDateOnly(d);
}

export function validateDateOfBirth(value: string, userType?: string, required = false): string | undefined {
  const minAge = minAgeForUserType(userType || '');
  if (!value.trim()) {
    return required ? 'Date of birth is required' : undefined;
  }
  const picked = new Date(`${value}T00:00:00`);
  if (Number.isNaN(picked.getTime())) return 'Enter a valid date of birth';
  const max = new Date(`${maxDateOfBirth(userType)}T00:00:00`);
  if (picked > max) return `Must be at least ${minAge} years old`;
  const min = new Date();
  min.setFullYear(min.getFullYear() - 120);
  if (picked < min) return 'Enter a realistic date of birth';
  return undefined;
}

export function validateUserForm(form: UserFormState, isEdit: boolean, tabId?: UserFormTabId): Record<string, string> {
  const errors: Record<string, string> = {};
  const cfg = getUserTypeFormConfig(form.userType);
  const mobileOnly = cfg.mobileLoginOnly;
  const isStaff = STAFF_USER_TYPES.has(form.userType);

  const checkProfile = !tabId || tabId === 'profile';
  const checkAssignment = !tabId || tabId === 'assignment';
  const checkPartner = !tabId || tabId === 'partner';
  const checkAccess = !tabId || tabId === 'access';
  const checkSecurity = (!tabId || tabId === 'security') && isStaff;

  if (!form.userType) {
    errors.userType = 'Select an account type';
    return errors;
  }

  if (checkProfile) {
    const firstNameError = validatePersonName(form.firstName, 'First name', { required: true });
    if (firstNameError) errors.firstName = firstNameError;
    const lastNameError = validatePersonName(form.lastName, 'Last name');
    if (lastNameError) errors.lastName = lastNameError;

    // Mobile app users: phone is required (OTP). Staff: email is required (web login).
    if (mobileOnly) {
      if (!form.phone.trim()) errors.phone = 'Phone number is required for mobile app login';
    } else if (isStaff) {
      if (!form.email.trim()) errors.email = 'Email is required for admin web login';
    } else if (!form.email.trim() && !form.phone.trim()) {
      errors.contact = 'Email or phone is required';
    }

    const emailError = validateEmail(form.email, 'Email', {
      required: isStaff || (!mobileOnly && !form.phone.trim()),
    });
    if (emailError) errors.email = emailError;
    if (form.phone.trim() && !form.phoneCountryCode) errors.phone = 'Select a country code';
    const phoneError = validateLocalPhoneForCountry(
      form.phone,
      phoneLengthForDialCode(form.phoneCountryCode),
      'Phone number',
      { required: mobileOnly || PARTNER_USER_TYPES.has(form.userType) },
    );
    if (phoneError) errors.phone = phoneError;
    if (form.emergencyPhone.trim() && !form.emergencyPhoneCountryCode) {
      errors.emergencyPhone = 'Select a country code';
    }
    const emergencyError = validateLocalPhoneForCountry(
      form.emergencyPhone,
      phoneLengthForDialCode(form.emergencyPhoneCountryCode),
      'Emergency contact',
    );
    if (emergencyError) errors.emergencyPhone = emergencyError;
    const dobRequired = PARTNER_USER_TYPES.has(form.userType);
    const dobError = validateDateOfBirth(form.dateOfBirth, form.userType, dobRequired);
    if (dobError) errors.dateOfBirth = dobError;
  }

  if (checkAssignment) {
    if (cfg.requiresZone && !form.zoneId) errors.zoneId = 'Operating zone is required for this user type';
    if (cfg.requiresServiceHub && !form.serviceHub) errors.serviceHub = 'Service hub is required for this user type';
  }

  if (checkPartner) {
    if (['merchant', 'vendor', 'service_provider'].includes(form.userType) && !form.businessName.trim()) {
      errors.businessName = 'Business / store name is required';
    }
  }

  if (checkAccess && isStaff && !(form.roleIds || []).length) {
    errors.roleIds = 'Assign at least one role';
  }

  // Web password only for employee / client_admin
  if (checkSecurity && !isEdit) {
    if (!form.password.trim()) errors.password = 'Password is required for admin web login';
    if (form.password && form.password.length < 6) errors.password = 'Password must be at least 6 characters';
    if (form.password && form.password !== form.confirmPassword) errors.confirmPassword = 'Passwords do not match';
  }

  if (checkSecurity && isEdit && form.password.trim()) {
    if (form.password.length < 6) errors.password = 'Password must be at least 6 characters';
    if (form.password !== form.confirmPassword) errors.confirmPassword = 'Passwords do not match';
  }

  return errors;
}
